/**
 * Shared hurricane-prep content and logic, used by both the Home page's
 * contextual alert cue and the standalone Prepare page. Kept in one place
 * so the resilience blurb and shelter link can't drift between the two.
 */

import type { LocalityGroup } from '../../shared/locality';

// Storm-resilience context, sourced from the July 2025 Babcock Ranch Media
// Kit (see docs/media-kit-extraction.md, pages 14-15). Deliberately framed as
// track record rather than a promise: the town came through Ian, Helene and
// Milton without losing power, internet or water, but that is history, not a
// guarantee for the next storm.
export const STORM_RESILIENCE =
  'The town sits at 23.9+ ft elevation, outside all flood zones, with underground power and a 150 MW solar plus 10 MW battery supply. Through Ian (100 mph sustained for 8 hours), Helene and Milton it held power, internet and water. Past performance, not a promise.';
export const SHELTER_SLUG = 'babcock-high-school-field-house';

/**
 * Deliberately NOT a hardcoded zone letter. Research for this feature
 * (2026-09-04) found Babcock Ranch widely reported as inland/elevated and
 * outside Charlotte County's storm-surge evacuation zones — plausible
 * given the county itself sited a shelter there, which counties don't do
 * inside an active surge zone — but no official county document naming a
 * specific zone (or an explicit "Non-Evacuation Zone" label for this
 * parcel) could be confirmed live. Evacuation zones are also a different
 * thing from flood zones and from wind/inland-flooding/road-closure risk,
 * so "outside the surge zone" is not "no risk". Send people to the
 * county's own live tool rather than asserting a zone in our copy.
 */
export const EVACUATION_ZONE_LOOKUP_URL = 'https://knowyourzone.charlottecountyfl.gov/';
export const CHARLOTTE_COUNTY_EM_URL = 'https://www.charlottecountyfl.gov/departments/emergency-management/';
export const CHARLOTTE_COUNTY_STORM_RECOVERY_URL =
  'https://www.charlottecountyfl.gov/departments/emergency-management/storm-recovery.stml';
export const BABCOCK_RANCH_HURRICANE_PREP_URL = 'https://www.babcockranchliving.com/422/Hurricane-Preparedness';
export const BABCOCK_RANCH_HURRICANE_PROTECTIONS_URL = 'https://www.babcockranchliving.com/543/Hurricane-Protections';

export type FilterablePlace = {
  category: string;
  tags: string[];
};

/**
 * Places worth calling out on the Prepare page as somewhere to buy storm
 * supplies: hardware/home-improvement (plywood, batteries, generators),
 * gas stations (fuel the car and any gas cans), and anywhere tagged
 * `propane` (grill/generator bottles). Mirrors the category-or-tag
 * pattern in placeFilter.ts rather than inventing a new one.
 */
const SUPPLY_CATEGORIES = new Set(['home_improvement', 'gas']);
const SUPPLY_TAGS = new Set(['propane']);

export function isStormSupplyPlace(place: FilterablePlace): boolean {
  return SUPPLY_CATEGORIES.has(place.category) || place.tags.some((t) => SUPPLY_TAGS.has(t));
}

export interface ChecklistItem {
  id: string;
  label: string;
  /**
   * Where the task happens. `inside` = at home / in-Ranch (Publix, Ace).
   * `outside` = a drive out — every gas and propane vendor in the dataset
   * is outside, so fuel errands live there. Same groups Places uses.
   */
  locality: LocalityGroup;
}

export interface ChecklistGroup {
  id: string;
  title: string;
  items: ChecklistItem[];
}

export const PREP_LOCALITY_ORDER: LocalityGroup[] = ['inside', 'outside'];

// Standard categories drawn from Ready.gov / FEMA hurricane-supply
// guidance, not tied to any one storm — general safety guidance, not a
// factual claim about a specific place that needs a citation.
export const HURRICANE_CHECKLIST: ChecklistGroup[] = [
  {
    id: 'water-food',
    title: 'Water & food',
    items: [
      { id: 'water', locality: 'inside', label: '1 gallon of water per person per day, 3+ days' },
      { id: 'food', locality: 'inside', label: 'Non-perishable food for 3+ days, plus a manual can opener' },
      { id: 'ice', locality: 'inside', label: 'Freeze jugs of water / stock ice in case the fridge-freezer loses power' },
    ],
  },
  {
    id: 'meds-docs',
    title: 'Medications & documents',
    items: [
      { id: 'meds', locality: 'inside', label: 'Refill prescriptions; pack a 2-week supply' },
      { id: 'firstaid', locality: 'inside', label: 'First aid kit' },
      { id: 'docs', locality: 'inside', label: 'Copies of ID, insurance and lease/deed — physical plus a phone photo backup' },
      { id: 'cash', locality: 'inside', label: 'Cash on hand — card readers and ATMs may be down' },
    ],
  },
  {
    id: 'power-fuel',
    title: 'Power & fuel',
    items: [
      { id: 'gas-car', locality: 'outside', label: 'Fill up the car and any gas cans' },
      { id: 'propane', locality: 'outside', label: 'Check propane and generator fuel levels' },
      { id: 'batteries', locality: 'inside', label: 'Batteries, flashlights, a battery or hand-crank radio' },
      { id: 'powerbank', locality: 'inside', label: 'Charge phones and any portable battery packs' },
    ],
  },
  {
    id: 'home-yard',
    title: 'Home & yard',
    items: [
      { id: 'shutters', locality: 'inside', label: 'Install shutters or board windows' },
      { id: 'furniture', locality: 'inside', label: 'Bring in or secure outdoor furniture, grills and planters' },
      { id: 'gutters', locality: 'inside', label: 'Clear gutters and drains' },
    ],
  },
  {
    id: 'pets-people',
    title: 'Pets & people',
    items: [
      { id: 'pet-supplies', locality: 'inside', label: 'Pet food, water, medications and a carrier' },
      { id: 'checkin', locality: 'inside', label: 'Check on neighbors, especially anyone elderly or alone' },
      { id: 'alerts', locality: 'inside', label: 'Sign up for Charlotte County / CivicReady emergency alerts' },
    ],
  },
];

/**
 * Split each category group into the two locality buckets, dropping empty
 * leftovers so “Power & fuel” can sit in both (batteries at home, gas on
 * the drive out) without blank sections.
 */
export function groupChecklistByLocality(
  groups: readonly ChecklistGroup[] = HURRICANE_CHECKLIST,
): Record<LocalityGroup, ChecklistGroup[]> {
  const out: Record<LocalityGroup, ChecklistGroup[]> = { inside: [], outside: [] };
  for (const group of groups) {
    for (const locality of PREP_LOCALITY_ORDER) {
      const items = group.items.filter((item) => item.locality === locality);
      if (items.length > 0) out[locality].push({ ...group, items });
    }
  }
  return out;
}

export const CHECKLIST_STORAGE_KEY = 'bn:hurricanePrepChecked';
